/**
 * Helpers compartidos entre el Inspector del Catalogador (RunItemsBrowser) y
 * el panel Data Health (HiddenItemsTab / DubiousItemsTab).
 *
 * Idea: NO duplicar la lógica de chips de filtros, badges de status y
 * categorización de marca final. Ambos paneles importan desde acá.
 *
 * Lo que NO movemos acá:
 *  - La tabla completa del Inspector — sigue siendo dueña de CatalogadorPanel
 *    porque depende de `RunDoc`, paginación cursor-based y enrichment cache
 *    que no aplican a Data Health.
 *  - Drawer derecho: cada panel tiene su propia variante.
 */
import React from 'react';

// ============================================================================
// Brand final categorization (color-coded cell para "Marca final")
// ============================================================================

export type BrandFinalCategory = 'enriched' | 'confirmed' | 'changed' | 'rejected' | 'neutral';

export const BRAND_FINAL_CELL_CLASS: Record<BrandFinalCategory, string> = {
  enriched: 'bg-green-50 border-l-4 border-green-400',
  confirmed: 'bg-blue-50',
  changed: 'bg-yellow-50 border-l-4 border-yellow-400',
  rejected: 'bg-red-50 border-l-4 border-red-400',
  neutral: '',
};

/**
 * Compara marca origen vs final para coloreado de celda.
 *  - 'enriched'  : origen vacía + final con valor (verde — value-add)
 *  - 'confirmed' : origen === final (azul)
 *  - 'changed'   : origen != final, ambas presentes (amarillo — revisar)
 *  - 'rejected'  : origen presente + final vacía (rojo — wasted)
 *  - 'neutral'   : ambas vacías
 */
export function categorizeBrandFinal(originalRaw: unknown, finalRaw: unknown): BrandFinalCategory {
  const norm = (v: unknown) =>
    typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim();
  const original = norm(originalRaw);
  const finalV = norm(finalRaw);
  const hasOriginal = original.length > 0;
  const hasFinal = finalV.length > 0;
  if (!hasOriginal && !hasFinal) return 'neutral';
  if (!hasOriginal && hasFinal) return 'enriched';
  if (hasOriginal && !hasFinal) return 'rejected';
  return original.toLowerCase() === finalV.toLowerCase() ? 'confirmed' : 'changed';
}

// ============================================================================
// Confidence badge
// ============================================================================

export interface ConfidenceBadgeProps {
  value: number | null | undefined;
}

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ value }) => {
  if (value == null || Number.isNaN(value)) {
    return <span className="text-gray-400">—</span>;
  }
  const v = Number(value);
  const colorClass =
    v >= 0.9
      ? 'bg-emerald-100 text-emerald-700'
      : v >= 0.7
        ? 'bg-blue-100 text-blue-700'
        : v >= 0.5
          ? 'bg-amber-100 text-amber-700'
          : 'bg-red-100 text-red-700';
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums ${colorClass}`}
    >
      {v.toFixed(2)}
    </span>
  );
};

// ============================================================================
// Status badge para visibility / hidden reason
// ============================================================================

export type StatusBadgeKind =
  | 'visible'
  | 'hidden_out_of_scope'
  | 'hidden_low_confidence'
  | 'hidden_manual'
  | 'hidden_duplicate'
  | 'dubious'
  | 'error';

const STATUS_BADGE_CLASS: Record<StatusBadgeKind, string> = {
  visible: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  hidden_out_of_scope: 'bg-gray-200 text-gray-700 border-gray-300',
  hidden_low_confidence: 'bg-amber-100 text-amber-700 border-amber-200',
  hidden_manual: 'bg-purple-100 text-purple-700 border-purple-200',
  hidden_duplicate: 'bg-orange-100 text-orange-700 border-orange-200',
  dubious: 'bg-orange-100 text-orange-800 border-orange-200',
  error: 'bg-red-200 text-red-900 border-red-300',
};

const STATUS_BADGE_LABEL: Record<StatusBadgeKind, string> = {
  visible: 'Visible',
  hidden_out_of_scope: 'Fuera de scope',
  hidden_low_confidence: 'Baja conf.',
  hidden_manual: 'Manual',
  hidden_duplicate: 'Duplicado',
  dubious: 'Dudoso',
  error: 'Error',
};

export interface StatusBadgeProps {
  kind: StatusBadgeKind;
  label?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ kind, label }) => (
  <span
    className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded border ${STATUS_BADGE_CLASS[kind]}`}
  >
    {label ?? STATUS_BADGE_LABEL[kind]}
  </span>
);

// ============================================================================
// Filter chip (botón pequeño tipo pill)
// ============================================================================

export interface FilterChipProps {
  active: boolean;
  label: string;
  count?: number | null;
  onClick: () => void;
}

/**
 * Mismo estilo que INSPECT_FILTERS de CatalogadorPanel (line 3375).
 * Activo = azul; inactivo = blanco con borde gris.
 */
export const FilterChip: React.FC<FilterChipProps> = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    {label}
    {count != null && (
      <span className="tabular-nums opacity-70 ml-1">({count.toLocaleString('es-AR')})</span>
    )}
  </button>
);

// ============================================================================
// Checkbox patterns (selección de filas)
// ============================================================================

export interface RowCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  onClick?: (e: React.MouseEvent) => void;
  ariaLabel?: string;
}

export const RowCheckbox: React.FC<RowCheckboxProps> = ({ checked, onChange, onClick, ariaLabel }) => (
  <input
    type="checkbox"
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
    onClick={onClick}
    aria-label={ariaLabel || 'Seleccionar fila'}
    className="accent-blue-600 cursor-pointer w-3.5 h-3.5"
  />
);

// ============================================================================
// Formatters (fechas, precios)
// ============================================================================

/** Formato es-AR DD/MM/YYYY HH:mm (siempre 24h, NO UTC). */
export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Solo fecha, sin hora. */
export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Precio con miles. */
export function formatPrice(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${Math.round(n).toLocaleString('es-AR')}`;
}
